/* eslint-disable max-lines */
import {
  type AttendanceOnDemandIntegrationConfig,
  type UkgIntegrationConfig,
} from "@clipboard-health/contract-backend-main";
import { type WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isNil } from "@clipboard-health/util-ts";
import { type ScoreFreezeReason } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import type { FacilityRule } from "@src/appV2/Facilities/types";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { BreakConfirmationBottomSheet } from "@src/appV2/redesign/Shift/BreakConfirmationBottomSheet/BreakConfirmationBottomSheet";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import { MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { isBackgroundCheckError } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/utils/handleBackgroundCheckError";
import { NoteAcknowledgementAction } from "@src/appV2/Shifts/MandatoryBreakPolicy/types";
import { type InternalTimeClockTutorialStep } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";

import { useSkippableWait } from "../../../utils/useSkippableWait";
import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  ShiftStage,
  type TimekeepingData,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { InternalTimeClockTutorialBottomSheet } from "../TimekeepingIntegrations/InternalTimeClockTutorialBottomSheet";
import { UseInternalTimeClockContent } from "../TimekeepingIntegrations/UseInternalTimeClockContent";
import { AcceptNonInstantPayContent } from "./AcceptNonInstantPayContent";
import { ActionNotFoundContent } from "./ActionNotFoundContent";
import { ActionSkippedContent } from "./ActionSkippedContent";
import {
  DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS,
  TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE,
} from "./constants";
import { ExtendedRetryLoadingContent } from "./ExtendedRetryLoadingContent";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";
import { useExtendedTimekeepingRetry } from "./useExtendedTimekeepingRetry";
import { getClockInSuccessDescription } from "./utils/getClockInSuccessDescription";
import { getHasWorkerClockedInOnTime } from "./utils/getHasWorkerClockedInOnTime";
import { WorkplaceRulesReminderContent } from "./WorkplaceRulesReminderContent";

export interface ClockInTimekeepingIntegrationBottomSheetProps {
  modalState: UseModalState;
  config: UkgIntegrationConfig | AttendanceOnDemandIntegrationConfig;
  workerCredentials: {
    badgeNumber: string;
  };
  shiftId: string;
  shiftStartDate: Date;
  earliestGeofenceEntryTime?: Date;
  workerId: string;
  workplaceId: string;
  workplaceName: string;
  breakPolicyNoteId: string;
  requiresBreakPolicyAcknowledgement: boolean;
  workplaceRules?: FacilityRule[];
  shouldShowWorkplaceRulesReminder: boolean;
  isSecondShiftInDouble: boolean;
  targetArrivalTime?: Date;
  timezone?: string;
  isTimesheetEditDisabled: boolean;
  shouldEnforceNfcTimesheetBounds: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  shiftBookedAt?: Date;
  workerStage: WorkerStage;
  workerSubstage?: string;
  isClipboardScoreFreezeEnabled: boolean;
  clipboardScoreFreezeReason?: ScoreFreezeReason;
  isMissedPunchRequestEnabled: boolean;
  onTimekeepingActionCompleted: (props?: { nonInstantPayReason?: string }) => void;
  onTimekeepingActionFailed: (props: { error: string }) => void;
  onClockInFlowCompleted: () => void;
}

type ClockInTimekeepingIntegrationBottomSheetStep =
  | "use-time-clock"
  | "action-not-found"
  | "action-skipped"
  | "requesting-clock-in"
  | "requesting-non-instant-pay"
  | "clock-in-success"
  | "workplace-rules";

export function ClockInTimekeepingIntegrationBottomSheet(
  props: ClockInTimekeepingIntegrationBottomSheetProps
) {
  const {
    modalState,
    config,
    workerCredentials,
    shiftId,
    shiftStartDate,
    workerId,
    workplaceId,
    workplaceName,
    breakPolicyNoteId,
    requiresBreakPolicyAcknowledgement: requiresBreakPolicyAcknowledgementProperty,
    workplaceRules,
    shouldShowWorkplaceRulesReminder,
    isSecondShiftInDouble,
    earliestGeofenceEntryTime,
    targetArrivalTime,
    timezone,
    isTimesheetEditDisabled,
    shouldEnforceNfcTimesheetBounds,
    isArrivalTimeFeatureEnabled,
    shiftBookedAt,
    workerStage,
    workerSubstage,
    isClipboardScoreFreezeEnabled,
    clipboardScoreFreezeReason,
    isMissedPunchRequestEnabled,
    onTimekeepingActionCompleted,
    onTimekeepingActionFailed,
    onClockInFlowCompleted,
  } = props;

  const history = useHistory();

  const internalTimeClockTutorialModalState = useModalState();

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId);

  const firstStep: ClockInTimekeepingIntegrationBottomSheetStep = "use-time-clock";
  const [step, setStep] = useState<ClockInTimekeepingIntegrationBottomSheetStep>(firstStep);

  const { mutateAsync: recordTimekeepingAction, data: postClockInShift } =
    useRecordShiftTimekeepingAction();

  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();

  const {
    retryPhase: clockInRetryPhase,
    executeWithRetry: executeClockInWithRetry,
    cancel: cancelClockInRetry,
  } = useExtendedTimekeepingRetry<Awaited<ReturnType<typeof recordTimekeepingAction>>>();

  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const { wait, skip } = useSkippableWait();

  const [requiresBreakPolicyAcknowledgement, setRequiresBreakPolicyAcknowledgement] = useState(
    requiresBreakPolicyAcknowledgementProperty
  );

  const clockInTime = postClockInShift?.clockInOut?.start
    ? parseISO(postClockInShift.clockInOut.start)
    : undefined;

  const hasWorkerClockedInOnTime = getHasWorkerClockedInOnTime({
    shiftStartDate,
    clockInTime,
  });

  const abortFlowAndClose = useCallback(() => {
    skip();
    cancelClockInRetry();
    modalState.closeModal();
    setStep(firstStep);
  }, [firstStep, modalState, skip, cancelClockInRetry]);

  const completeSuccessFlow = useCallback(
    async (timekeepingData?: TimekeepingData) => {
      setStep("clock-in-success");
      await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);

      if (timekeepingData) {
        queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (result) =>
          result
            ? {
                ...result,
                response: {
                  ...result.response,
                  clockInOut: timekeepingData.clockInOut,
                },
              }
            : undefined
        );

        onClockInFlowCompleted();
      }
    },
    [wait, queryClient, shiftId, onClockInFlowCompleted]
  );

  const onClockInSuccess = useCallback(
    async (timekeepingData: TimekeepingData) => {
      onTimekeepingActionCompleted();

      if (shouldShowWorkplaceRulesReminder && workplaceRules && workplaceRules.length > 0) {
        setStep("workplace-rules");
      } else {
        await completeSuccessFlow(timekeepingData);
      }
    },
    [
      completeSuccessFlow,
      shouldShowWorkplaceRulesReminder,
      workplaceRules,
      onTimekeepingActionCompleted,
    ]
  );

  async function recordClockIn() {
    try {
      const postMutationShift = await executeClockInWithRetry(
        async () =>
          await recordTimekeepingAction({
            shiftId,
            stage: ShiftStage.CLOCK_IN,
            shiftActionCheck: ActionCheckType.INTEGRATION,
            verificationMethod: config.type,
          }),
        {
          shiftId,
          workerId,
          workplaceId,
          action: "clock-in",
        }
      );

      // If cancelled, postMutationShift will be undefined
      if (isNil(postMutationShift)) {
        return;
      }

      await onClockInSuccess(postMutationShift);
    } catch (error: unknown) {
      if (
        error instanceof Error &&
        error.message === TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE
      ) {
        setStep("action-not-found");
        return;
      }

      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock in, please try again.";

      onTimekeepingActionFailed({ error: errorMessage });

      if (isBackgroundCheckError(error)) {
        modalState.closeModal();
        history.replace(MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH);
        return;
      }

      showErrorToast(errorMessage);

      abortFlowAndClose();
    }
  }

  async function convertToNonInstantPay() {
    try {
      await markNonInstant({
        shiftId,
        reason: ShiftMarkedNonIpReasons.EXTERNAL_INTEGRATION_FAILURE,
      });

      onTimekeepingActionCompleted({
        nonInstantPayReason: "EXTERNAL_INTEGRATION_FAILURE",
      });

      modalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      abortFlowAndClose();
    }
  }

  return (
    <>
      {requiresBreakPolicyAcknowledgement && (
        <BreakConfirmationBottomSheet
          action={NoteAcknowledgementAction.CLOCK_IN}
          isLoading={false}
          modalState={modalState}
          shiftId={shiftId}
          workplaceId={workplaceId}
          breakPolicyNoteId={breakPolicyNoteId}
          onAcknowledge={async () => {
            setRequiresBreakPolicyAcknowledgement(false);
          }}
        />
      )}

      {!requiresBreakPolicyAcknowledgement && (
        <BottomSheet modalState={modalState} canDismiss={false}>
          {step === "use-time-clock" && (
            <UseInternalTimeClockContent
              action="clock-in"
              badgeNumber={workerCredentials.badgeNumber}
              pinsToEnter={config.pinsToEnterString ?? ""}
              timeclockLocation={config.timeclockLocationString ?? ""}
              onActionConfirm={async () => {
                setStep("requesting-clock-in");
                await recordClockIn();
              }}
              onHelp={() => {
                internalTimeClockTutorialModalState.openModal();
              }}
              onCancel={abortFlowAndClose}
            />
          )}
          {step === "action-not-found" && (
            <ActionNotFoundContent
              action="clock-in"
              onTryAgain={async () => {
                setStep("requesting-clock-in");
                await recordClockIn();
              }}
              onSkipAction={() => {
                setStep("action-skipped");
              }}
              onClose={abortFlowAndClose}
            />
          )}
          {step === "action-skipped" &&
            (isMissedPunchRequestEnabled ? (
              <AcceptNonInstantPayContent
                isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
                onAccept={async () => {
                  logEvent(APP_V2_USER_EVENTS.ALREADY_STARTED_WORKING_BUTTON_TAPPED, {
                    button: "direct-integration-fallback",
                    shiftId,
                    workerId,
                    workplaceId,
                  });
                  setStep("requesting-non-instant-pay");
                  await convertToNonInstantPay();
                }}
                onCancel={() => {
                  setStep("use-time-clock");
                }}
              />
            ) : (
              <ActionSkippedContent
                action="clock-in"
                onAccept={async () => {
                  setStep("requesting-non-instant-pay");
                  await convertToNonInstantPay();
                }}
                onCancel={() => {
                  setStep("use-time-clock");
                }}
              />
            ))}
          {step === "requesting-clock-in" && clockInRetryPhase !== "extended" && (
            <BottomSheetLoadingContent
              title="Confirming your clock in"
              description="Should take up to 30 seconds"
            />
          )}
          {step === "requesting-clock-in" && clockInRetryPhase === "extended" && (
            <ExtendedRetryLoadingContent
              action="clock-in"
              onCancel={abortFlowAndClose}
              onSkipAction={() => {
                setStep("action-skipped");
              }}
            />
          )}
          {step === "clock-in-success" && (
            <TimekeepingActionSuccessContent
              title="Have a great shift!"
              description={getClockInSuccessDescription({
                hasWorkerClockedInOnTime,
                isSecondShiftInDouble,
                earliestGeofenceEntryTime,
                isTimesheetEditDisabled,
                shouldEnforceNfcTimesheetBounds,
                isArrivalTimeFeatureEnabled,
                targetArrivalTime,
                shiftStartDate,
                clockInTime,
                timezone,
                shiftBookedAt,
                policy: clipboardScoreProfile?.policy,
                workerStage,
                workerSubstage,
                isClipboardScoreFreezeEnabled,
                clipboardScoreFreezeReason,
              })}
              onClose={abortFlowAndClose}
            />
          )}
          {step === "workplace-rules" && workplaceRules && (
            <WorkplaceRulesReminderContent
              shiftId={shiftId}
              workplaceId={workplaceId}
              workplaceRules={workplaceRules}
              onContinue={async () => {
                await completeSuccessFlow(postClockInShift);
              }}
            />
          )}
          {step === "requesting-non-instant-pay" && (
            <BottomSheetLoadingContent
              title="Converting to non-instant pay"
              description="Should only take a few seconds"
            />
          )}
        </BottomSheet>
      )}
      <InternalTimeClockTutorialBottomSheet
        action="clock-in"
        modalState={internalTimeClockTutorialModalState}
        steps={config.tutorial.stepsClockIn as InternalTimeClockTutorialStep[]}
        placeholders={{ workplaceName, badgeNumber: workerCredentials.badgeNumber }}
        onTimeclockActionPerformed={async () => {
          internalTimeClockTutorialModalState.closeModal();

          setStep("requesting-clock-in");
          await recordClockIn();
        }}
        onTimeclockNotWorking={() => {
          internalTimeClockTutorialModalState.closeModal();
          setStep("action-skipped");
        }}
      />
    </>
  );
}
/* eslint-enable max-lines */
